<template>
  <CCard class="cluster-result-card" @click="$emit('select', cluster)">
    <CCardBody class="text-center p-3">
      <!-- Face Thumbnail -->
      <div class="cluster-thumb-wrapper mb-2">
        <img
          v-if="cluster.thumb_url"
          :src="cluster.thumb_url"
          class="cluster-thumb"
          :alt="clusterName"
        />
        <div v-else class="cluster-thumb-placeholder">
          <CIcon name="cipUser" size="3xl"/>
        </div>
      </div>

      <!-- Person Name -->
      <div class="cluster-name mb-2">
        <strong>{{ clusterName }}</strong>
      </div>

      <!-- Confidence Bar -->
      <div class="confidence-section mb-2">
        <div class="confidence-bar">
          <div
            class="confidence-fill"
            :style="confidenceStyle"
          ></div>
        </div>
        <small class="confidence-text d-block mt-1">
          {{ confidenceLabel }}
        </small>
      </div>

      <!-- Face Count Badge -->
      <CBadge color="info">
        {{ cluster.face_count }} {{ cluster.face_count === 1 ? 'photo' : 'photos' }}
      </CBadge>
    </CCardBody>
  </CCard>
</template>

<script>
import { mapGetters } from 'vuex'
import { applyPattern, getClusterTags } from '@/domain/core/utils/pattern'

export default {
  name: "ClusterResultCard",
  emits: ['select'],
  props: {
    cluster: {
      type: Object,
      required: true
      // Expected: {id, confidence, thumb_url, face_count, picacode, tags}
    }
  },
  computed: {
    ...mapGetters('photoSearch', ['clusterNamePattern']),
    confidencePercent() {
      return Math.round(this.cluster.confidence * 100)
    },
    confidenceLabel() {
      const c = this.cluster.confidence
      if (c >= 0.98) return this.$pgettext('photosearch.identify', 'Excellent match')
      if (c >= 0.95) return this.$pgettext('photosearch.identify', 'Good match')
      if (c >= 0.90) return this.$pgettext('photosearch.identify', 'Moderate match')
      return this.$pgettext('photosearch.identify', 'Low match')
    },
    confidenceStyle() {
      const c = this.cluster.confidence
      let color = '#ffa500' // Orange
      if (c >= 0.98) color = '#28a745' // Green
      else if (c >= 0.95) color = '#ffc107' // Yellow

      return {
        width: `${this.confidencePercent}%`,
        backgroundColor: color,
        transition: 'width 0.3s ease'
      }
    },
    clusterName() {
      const tags = getClusterTags(this.cluster)
      const fallback = this.$pgettext('photosearch.identify', 'Unknown person')
      return applyPattern(tags, this.clusterNamePattern, fallback)
    }
  }
}
</script>

<style scoped>
.cluster-result-card {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 2px solid transparent;
}

.cluster-result-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  border-color: var(--primary);
}

.cluster-thumb-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.cluster-thumb {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #e0e0e0;
  transition: border-color 0.2s ease;
}

.cluster-result-card:hover .cluster-thumb {
  border-color: var(--primary);
}

.cluster-thumb-placeholder {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
}

.confidence-section {
  width: 100%;
}

.confidence-bar {
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.confidence-fill {
  height: 100%;
  border-radius: 4px;
}

.confidence-text {
  font-size: 12px;
  color: #666;
}

.cluster-name {
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
